/**
 * GeogridLcrBridge - Adapter between ProductDataService (unified JSON) and LCR calculation engine.
 *
 * This bridge resolves the "two sources of truth" problem:
 * - Before: LcrCalculationService read from pavement-database.json (static, bundled)
 * - After: LcrCalculationService reads from this bridge, which pulls from ProductDataService
 *   (dynamic, API-backed, admin-editable) with pavement-database.json as fallback.
 *
 * ID Mapping: pavement-database.json uses 'multicomposito' but unified JSON uses 'Mult. MacDrain IW'.
 * The bridge handles this mapping transparently.
 */

import { ProductDataService } from './ProductDataService';
import pavementDatabase from './pavement-database.json';

export interface LcrEquation {
    m2: number;
    m1: number;
    c: number;
    threshold: number;
}

export interface GeogridLcrEntry {
    id: string;
    name: string;
    equation: LcrEquation;
    lcrBase?: number;
    lcrSubbase?: number;
    grade?: number;
    drainageCoefficientOverride?: number | null;
    databaseReference?: string;
}

/**
 * ID aliases: maps legacy IDs in pavement-database.json to unified JSON IDs.
 * When the calculation engine requests 'multicomposito', the bridge looks up 'Mult. MacDrain IW'.
 */
const ID_ALIASES: Record<string, string> = {
    multicomposito: 'Mult. MacDrain IW',
};

/** Reverse mapping: unified JSON ID → legacy IDs that should also be exposed. */
const REVERSE_ALIASES: Record<string, string[]> = {
    'Mult. MacDrain IW': ['multicomposito'],
};

let cachedEquations: Record<string, LcrEquation> | null = null;
let cachedEntries: GeogridLcrEntry[] | null = null;
let cacheFromFallback = false;

/**
 * Extract LCR equations from ProductDataService (unified JSON).
 * Uses getAllUnifiedProducts() to get raw IUnifiedProduct data with LCR fields.
 * Returns null if ProductDataService is not initialized.
 */
function buildFromProductDataService(): { equations: Record<string, LcrEquation>; entries: GeogridLcrEntry[] } | null {
    if (!ProductDataService.isInitialized()) {
        return null;
    }

    const equations: Record<string, LcrEquation> = {};
    const entries: GeogridLcrEntry[] = [];

    const allFamilies = ProductDataService.getAllUnifiedProducts();

    for (const [, products] of Object.entries(allFamilies)) {
        for (const product of products) {
            if (!product.lcrEquation) {
                continue;
            }

            const equation: LcrEquation = {
                m2: product.lcrEquation.m2,
                m1: product.lcrEquation.m1,
                c: product.lcrEquation.c,
                threshold: product.lcrEquation.threshold,
            };

            const entry: GeogridLcrEntry = {
                id: product.id,
                name: product.prodotto,
                equation,
                lcrBase: product.lcrBase ?? undefined,
                lcrSubbase: product.lcrSubbase ?? undefined,
                grade: product.grade ?? undefined,
                drainageCoefficientOverride: product.drainageCoefficientOverride,
                databaseReference: product.databaseReference ?? undefined,
            };

            equations[product.id] = equation;
            entries.push(entry);

            // Add reverse aliases so legacy IDs also resolve
            const aliases = REVERSE_ALIASES[product.id];
            if (aliases) {
                for (const alias of aliases) {
                    equations[alias] = equation;
                    entries.push({ ...entry, id: alias });
                }
            }
        }
    }

    return { equations, entries };
}

/**
 * Extract LCR equations from pavement-database.json (static fallback).
 */
function buildFromPavementDatabase(): { equations: Record<string, LcrEquation>; entries: GeogridLcrEntry[] } {
    const equations: Record<string, LcrEquation> = {};
    const entries: GeogridLcrEntry[] = [];

    for (const geogrid of pavementDatabase.geogrids) {
        if (geogrid.equation && geogrid.id !== 'None') {
            const equation = geogrid.equation as LcrEquation;
            equations[geogrid.id] = equation;
            entries.push({
                id: geogrid.id,
                name: geogrid.name,
                equation,
                lcrBase: (geogrid as Record<string, unknown>).lcrBase as number | undefined,
                lcrSubbase: (geogrid as Record<string, unknown>).lcrSubbase as number | undefined,
                grade: (geogrid as Record<string, unknown>).grade as number | undefined,
                drainageCoefficientOverride: geogrid.drainageCoefficientOverride,
                databaseReference: (geogrid as Record<string, unknown>).databaseReference as string | undefined,
            });
        }
    }

    return { equations, entries };
}

function ensureCache(): { equations: Record<string, LcrEquation>; entries: GeogridLcrEntry[] } {
    // If cache was built from fallback but ProductDataService is now ready, rebuild from API
    if (cachedEquations && cachedEntries && cacheFromFallback && ProductDataService.isInitialized()) {
        cachedEquations = null;
        cachedEntries = null;
        cacheFromFallback = false;
    }

    if (cachedEquations && cachedEntries) {
        return { equations: cachedEquations, entries: cachedEntries };
    }

    const fromApi = buildFromProductDataService();
    if (fromApi && Object.keys(fromApi.equations).length > 0) {
        cachedEquations = fromApi.equations;
        cachedEntries = fromApi.entries;
        cacheFromFallback = false;
        return fromApi;
    }

    const fromStatic = buildFromPavementDatabase();
    cachedEquations = fromStatic.equations;
    cachedEntries = fromStatic.entries;
    cacheFromFallback = true;
    return fromStatic;
}

export const GeogridLcrBridge = {
    /**
     * Get all LCR equations as Record<geogridId, LcrEquation>.
     * Primary: ProductDataService (unified JSON). Fallback: pavement-database.json.
     */
    getEquations(): Record<string, LcrEquation> {
        return ensureCache().equations;
    },

    /**
     * Get a single equation by geogrid ID. Resolves aliases.
     */
    getEquation(geogridId: string): LcrEquation | undefined {
        const equations = ensureCache().equations;
        // Exact match
        if (equations[geogridId]) return equations[geogridId];
        // Alias resolution (e.g., 'multicomposito' → 'Mult. MacDrain IW')
        const resolvedId = ID_ALIASES[geogridId];
        if (resolvedId && equations[resolvedId]) return equations[resolvedId];
        // Case-insensitive fallback: normalize to lowercase with underscores
        const normalizedId = geogridId.toLowerCase().replace(/[^a-z0-9_]/g, '_');
        if (equations[normalizedId]) return equations[normalizedId];
        // Case-insensitive alias check
        const lowerAlias = ID_ALIASES[normalizedId];
        return lowerAlias ? equations[lowerAlias] : undefined;
    },

    /**
     * Get all geogrid entries with LCR data (id, name, equation, lcrBase, lcrSubbase, grade).
     */
    getEntries(): GeogridLcrEntry[] {
        return ensureCache().entries;
    },

    /**
     * Find a specific entry by geogrid ID. Resolves aliases.
     */
    findEntry(geogridId: string): GeogridLcrEntry | undefined {
        const resolvedId = ID_ALIASES[geogridId] || geogridId;
        const entries = ensureCache().entries;
        return entries.find(e => e.id === geogridId) || entries.find(e => e.id === resolvedId);
    },

    /**
     * Invalidate the cache. Call after admin product edits + forceRefresh().
     */
    invalidateCache(): void {
        cachedEquations = null;
        cachedEntries = null;
        cacheFromFallback = false;
    },
};
